// src/Dashboard/pages/user/Messaging/components/ComposeMessageDrawer.jsx

import React, { useEffect, useMemo, useState } from "react";
import Select, { components } from "react-select";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { FaPaperPlane, FaTimes } from "react-icons/fa";
import { toast } from "react-toastify";

import { getAllUsers } from "../../../../../services/userServices";

import {
  MESSAGE_SUBJECT_OPTIONS,
  getUserAvatar,
  getUserFullName,
  normalizeText,
  roleMeta,
} from "../utils/messagingUtils";

const quillModules = {
  toolbar: [
    [{ header: [2, 3, false] }],
    ["bold", "italic", "underline"],
    [{ list: "ordered" }, { list: "bullet" }],
    ["link", "clean"],
  ],
};

const quillFormats = [
  "header",
  "bold",
  "italic",
  "underline",
  "list",
  "bullet",
  "link",
];

const CustomOption = (props) => {
  const { data } = props;
  const role = roleMeta(data.role_id);

  return (
    <components.Option {...props}>
      <div className="flex items-center gap-3">
        <img
          src={data.photo}
          alt={data.label}
          className="h-8 w-8 rounded-xl object-cover"
        />

        <div className="min-w-0">
          <p className="truncate text-sm font-bold">{data.label}</p>

          <div className="mt-1 flex flex-wrap gap-1">
            <span
              className={`rounded-full px-2 py-0.5 text-[10px] font-bold ring-1 ${role.className}`}
            >
              {role.label}
            </span>

            {data.secondary && (
              <span className="text-[11px] opacity-70">{data.secondary}</span>
            )}
          </div>
        </div>
      </div>
    </components.Option>
  );
};

const CustomSingleValue = (props) => {
  const { data } = props;

  return (
    <components.SingleValue {...props}>
      <div className="flex items-center gap-2">
        <img
          src={data.photo}
          alt={data.label}
          className="h-6 w-6 rounded-lg object-cover"
        />
        <span>{data.label}</span>
      </div>
    </components.SingleValue>
  );
};

const filterOption = (option, inputValue) => {
  const data = option.data || {};
  const haystack = [
    data.label,
    data.secondary,
    data.roleLabel,
    data.adherentsText,
  ]
    .map(normalizeText)
    .join(" ");

  return haystack.includes(normalizeText(inputValue));
};

export default function ComposeMessageDrawer({
  isOpen,
  mode = "create",
  userId,
  userRole,
  initialRecipientId = "",
  initialSubject = "",
  lockedRecipient,
  onClose,
  onSend,
}) {
  const [recipients, setRecipients] = useState([]);
  const [loadingRecipients, setLoadingRecipients] = useState(false);
  const [sending, setSending] = useState(false);

  const [recipientId, setRecipientId] = useState(initialRecipientId || "");
  const [subjectChoice, setSubjectChoice] = useState("");
  const [customSubject, setCustomSubject] = useState("");
  const [content, setContent] = useState("");

  const isReply = mode === "reply";
  const isSimpleUser = userRole !== "admin" && userRole !== "enseignant";

  useEffect(() => {
    if (!isOpen) return;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        onClose?.();
      }
    };

    window.addEventListener("keydown", onKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [isOpen, onClose]);

  useEffect(() => {
    if (!isOpen) return;

    setRecipientId(initialRecipientId || "");
    setContent("");

    const knownSubject = MESSAGE_SUBJECT_OPTIONS.find(
      (item) => item.toLowerCase() === String(initialSubject || "").toLowerCase()
    );

    if (initialSubject) {
      setSubjectChoice(knownSubject || "Autre (saisir)");
      setCustomSubject(knownSubject ? "" : initialSubject);
    } else {
      setSubjectChoice("");
      setCustomSubject("");
    }
  }, [isOpen, initialRecipientId, initialSubject]);

  useEffect(() => {
    if (!isOpen || lockedRecipient) return;

    let cancelled = false;

    const loadRecipients = async () => {
      try {
        setLoadingRecipients(true);

        const data = await getAllUsers();
        let users = Array.isArray(data) ? data : [];

        users = users.filter((item) => Number(item.id) !== Number(userId));

        if (isSimpleUser) {
          users = users.filter(
            (item) =>
              [1, 3].includes(Number(item.role_id)) &&
              (item.receive_messages === true ||
                item.receive_messages === 1 ||
                item.receive_messages === "1")
          );
        }

        if (!cancelled) {
          setRecipients(users);
        }
      } catch (error) {
        console.error("Erreur chargement destinataires :", error);
        toast.error("Erreur lors du chargement des destinataires.");
      } finally {
        if (!cancelled) {
          setLoadingRecipients(false);
        }
      }
    };

    loadRecipients();

    return () => {
      cancelled = true;
    };
  }, [isOpen, isSimpleUser, lockedRecipient, userId]);

  const options = useMemo(() => {
    return recipients.map((recipient) => {
      const adherentsText = Array.isArray(recipient.adherents)
        ? recipient.adherents
            .map((adherent) => `${adherent.prenom || ""} ${adherent.nom || ""}`)
            .join(", ")
        : "";

      const role = roleMeta(recipient.role_id);

      return {
        value: recipient.id,
        label: getUserFullName(recipient),
        photo: getUserAvatar(recipient),
        role_id: recipient.role_id,
        roleLabel: role.label,
        secondary: adherentsText ? `Adhérents : ${adherentsText}` : "",
        adherentsText,
      };
    });
  }, [recipients]);

  const selectedOption = useMemo(() => {
    return options.find((option) => Number(option.value) === Number(recipientId)) || null;
  }, [options, recipientId]);

  const finalSubject =
    subjectChoice === "Autre (saisir)"
      ? customSubject.trim()
      : subjectChoice.trim();

  const canSubmit =
    !!recipientId && !!finalSubject && !!content && content !== "<p><br></p>";

  if (!isOpen) return null;

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!canSubmit) {
      toast.error("Veuillez renseigner le destinataire, l’objet et le message.");
      return;
    }

    try {
      setSending(true);

      await onSend?.({
        recipient_id: recipientId,
        subject: finalSubject,
        content,
      });
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[120]">
      <div
        className="absolute inset-0 bg-slate-950/60 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden
      />

      <aside className="absolute inset-y-0 right-0 flex w-full max-w-4xl flex-col overflow-hidden bg-white shadow-2xl dark:bg-slate-900">
        <div className="h-1.5 bg-gradient-to-r from-[#013E8E] via-indigo-500 to-[#4CA82F]" />

        <header className="flex items-center justify-between gap-3 border-b border-slate-200 px-4 py-4 dark:border-slate-800 sm:px-6">
          <div className="min-w-0">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500 dark:text-slate-400">
              Message privé
            </p>

            <h2 className="truncate text-lg font-bold text-slate-900 dark:text-white sm:text-xl">
              {isReply ? "Répondre à un message" : "Nouveau message"}
            </h2>
          </div>

          <button
            type="button"
            onClick={onClose}
            disabled={sending}
            className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-slate-100 text-slate-700 transition hover:bg-slate-200 disabled:opacity-60 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700"
          >
            <FaTimes />
          </button>
        </header>

        <form onSubmit={handleSubmit} className="flex flex-1 flex-col overflow-hidden">
          <div className="flex-1 overflow-y-auto p-4 sm:p-6">
            <div className="space-y-5">
              <div>
                <label className="mb-1 block text-sm font-bold text-slate-700 dark:text-slate-200">
                  Destinataire
                </label>

                {lockedRecipient ? (
                  <div className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 dark:border-slate-700 dark:bg-slate-950">
                    <img
                      src={getUserAvatar(lockedRecipient)}
                      alt={getUserFullName(lockedRecipient)}
                      className="h-10 w-10 rounded-2xl object-cover"
                    />

                    <p className="font-bold text-slate-900 dark:text-white">
                      {getUserFullName(lockedRecipient)}
                    </p>
                  </div>
                ) : loadingRecipients ? (
                  <div className="h-12 animate-pulse rounded-2xl bg-slate-200 dark:bg-slate-800" />
                ) : (
                  <Select
                    classNamePrefix="rs"
                    options={options}
                    value={selectedOption}
                    onChange={(option) => setRecipientId(option?.value || "")}
                    placeholder="Sélectionnez un destinataire..."
                    isClearable
                    components={{
                      Option: CustomOption,
                      SingleValue: CustomSingleValue,
                    }}
                    filterOption={filterOption}
                    noOptionsMessage={() => "Aucun destinataire"}
                    menuPortalTarget={document.body}
                    styles={{
                      control: (base, state) => ({
                        ...base,
                        minHeight: 48,
                        borderRadius: 16,
                        backgroundColor: "transparent",
                        borderColor: state.isFocused ? "#6366f1" : "#cbd5e1",
                        boxShadow: "none",
                      }),
                      menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                    }}
                  />
                )}
              </div>

              <div className="grid gap-4 lg:grid-cols-[280px_minmax(0,1fr)]">
                <div>
                  <label className="mb-1 block text-sm font-bold text-slate-700 dark:text-slate-200">
                    Objet
                  </label>

                  <select
                    value={subjectChoice}
                    onChange={(event) => {
                      setSubjectChoice(event.target.value);
                      if (event.target.value !== "Autre (saisir)") {
                        setCustomSubject("");
                      }
                    }}
                    className="w-full rounded-2xl border border-slate-300 bg-white px-4 py-3 text-slate-900 outline-none transition focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-950 dark:text-white"
                  >
                    <option value="">Sélectionner un objet</option>
                    {MESSAGE_SUBJECT_OPTIONS.map((subject) => (
                      <option key={subject} value={subject}>
                        {subject}
                      </option>
                    ))}
                  </select>
                </div>

                {subjectChoice === "Autre (saisir)" && (
                  <div>
                    <label className="mb-1 block text-sm font-bold text-slate-700 dark:text-slate-200">
                      Objet personnalisé
                    </label>

                    <input
                      type="text"
                      value={customSubject}
                      onChange={(event) => setCustomSubject(event.target.value)}
                      className="w-full rounded-2xl border border-slate-300 bg-white px-4 py-3 text-slate-900 outline-none transition focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-950 dark:text-white"
                      placeholder="Ex. : Question sur la tenue..."
                    />
                  </div>
                )}
              </div>

              <div>
                <label className="mb-1 block text-sm font-bold text-slate-700 dark:text-slate-200">
                  Message
                </label>

                <div className="overflow-hidden rounded-2xl border border-slate-300 bg-white dark:border-slate-700 dark:bg-slate-950">
                  <ReactQuill
                    theme="snow"
                    value={content}
                    onChange={setContent}
                    modules={quillModules}
                    formats={quillFormats}
                    placeholder="Rédigez votre message..."
                  />
                </div>
              </div>
            </div>
          </div>

          <footer className="border-t border-slate-200 bg-white/95 p-4 dark:border-slate-800 dark:bg-slate-900/95">
            <div className="flex flex-col gap-2 sm:flex-row sm:justify-end">
              <button
                type="button"
                onClick={onClose}
                disabled={sending}
                className="inline-flex items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-bold text-slate-700 transition hover:bg-slate-50 disabled:opacity-60 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
              >
                <FaTimes />
                Annuler
              </button>

              <button
                type="submit"
                disabled={sending || !canSubmit}
                className="inline-flex items-center justify-center gap-2 rounded-2xl bg-[#013E8E] px-4 py-3 text-sm font-bold text-white transition hover:bg-indigo-800 disabled:cursor-not-allowed disabled:opacity-60"
              >
                <FaPaperPlane />
                {sending ? "Envoi..." : "Envoyer"}
              </button>
            </div>
          </footer>
        </form>
      </aside>
    </div>
  );
}